import { Text, VStack } from "@chakra-ui/react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "@common/interfaces/adsManagerGuidedEdit";
import { CampaignRunRangePicker } from "./CampaignRunRangePicker";

/** Match phase schedule headings in {@link GuidedBudgetEditor}. */
const scheduleHeadingColor = "#1A202C";

export type CampaignRunScheduleBlockProps = {
  /** IANA zone for the linked event; wall times below are interpreted here. */
  eventTimezone?: string;
  startAt: string;
  endAt: string;
  onChangeStartAt: (value: string) => void;
  onChangeEndAt: (value: string) => void;
  /** When true, start is shown read-only (e.g. Meta-synced campaign after schedule has started). */
  isStartAtDisabled?: boolean;
  /** When true, both start and end are read-only (e.g. live campaign with phased scheduled budget). */
  isScheduleRangeReadOnly?: boolean;
  /** Shown under the range when {@link isScheduleRangeReadOnly} is true. */
  scheduleReadOnlyHelperText?: string;
  /**
   * When true, calendar open icons sit before the datetime value in each picker. The create
   * flow’s {@link GuidedBudgetEditor} run-schedule card and {@link GuidedScheduleEditor} set this;
   * phased budget rows keep the default (icon after the value).
   */
  pickerOpenIconAtStart?: boolean;
};

/**
 * Campaign run start/end via {@link CampaignRunRangePicker}. Includes its own
 * {@link LocalizationProvider} so it can render inside or outside the phased-budget picker tree.
 */
export function CampaignRunScheduleBlock({
  eventTimezone = GUIDED_EDIT_DEFAULT_TIMEZONE,
  startAt,
  endAt,
  onChangeStartAt,
  onChangeEndAt,
  isStartAtDisabled = false,
  isScheduleRangeReadOnly = false,
  scheduleReadOnlyHelperText,
  pickerOpenIconAtStart = false,
}: CampaignRunScheduleBlockProps) {
  const tz = eventTimezone;
  const lockFullRange = isScheduleRangeReadOnly === true;

  return (
    <VStack align="stretch" spacing={3} w="full">
      <Text
        fontSize="md"
        fontWeight="semibold"
        color={scheduleHeadingColor}
        lineHeight="1.3"
      >
        Schedule (start to end)
      </Text>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CampaignRunRangePicker
          eventTimezone={tz}
          startAt={startAt}
          endAt={endAt}
          onChangeStartAt={onChangeStartAt}
          onChangeEndAt={onChangeEndAt}
          isStartAtDisabled={isStartAtDisabled}
          isScheduleRangeReadOnly={lockFullRange}
          pickerOpenIconAtStart={pickerOpenIconAtStart}
        />
      </LocalizationProvider>
      {lockFullRange && scheduleReadOnlyHelperText?.trim() ? (
        <Text fontSize="sm" color="gray.600" lineHeight="short">
          {scheduleReadOnlyHelperText.trim()}
        </Text>
      ) : null}
    </VStack>
  );
}
